import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiMapPin, FiAlertCircle } from 'react-icons/fi';
import './FeaturedDestinations.css';
import Api from "../../../Api.js";

const SKELETON_COUNT = 4;

export default function FeaturedDestinations() {
  const [destinationsData, setDestinationsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchDestinations = async () => {
      try {
        setLoading(true);
        const response = await Api.get('/wedding/destinations', {
          signal: abortController.signal,
        });

        const payload = response.data || response;

        if (payload.status && Array.isArray(payload.data) && payload.data.length > 0) {
          const mappedDestinations = payload.data.map((item) => ({
            id: item.id,
            name: item.name,
            image: item.featured_photo_url?.startsWith('http')
              ? item.featured_photo_url
              : `https://admintest.jayrajgroup.org${item.featured_photo_url || ''}`,
            link: `/destination-wedding-packages/${item.id}`,
            state: item.state?.name,
            city: item.city?.name,
            shortDescription: item.short_description,
            packagesCount: item.packages_count,
          }));
          setDestinationsData(mappedDestinations);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          console.error('Failed to fetch destinations:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
    return () => abortController.abort();
  }, []);

  return (
    <section className="fd-section">
      <span className="fd-orb fd-orb-a" aria-hidden="true" />
      <span className="fd-orb fd-orb-b" aria-hidden="true" />

      <div className="fd-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="fd-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
  
          <h2 className="fd-title">Best Wedding Places in India</h2>
          <p className="fd-subtitle">
            Handpicked locations where your celebration becomes a memory
          </p>
        </motion.header>

        {/* ---------- Body states ---------- */}
        {error ? (
          <div className="fd-empty fd-empty-error">
            <span className="fd-empty-icon">
              <FiAlertCircle />
            </span>
            <h3 className="fd-empty-title">Couldn’t load destinations</h3>
            <p className="fd-empty-text">Please try again in a moment.</p>
          </div>
        ) : loading ? (
          <div className="fd-grid" aria-busy="true" aria-label="Loading destinations">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div key={i} className="fd-skeleton" />
            ))}
          </div>
        ) : destinationsData.length === 0 ? (
          <div className="fd-empty">
            <span className="fd-empty-icon">
              <FiAlertCircle />
            </span>
            <h3 className="fd-empty-title">No destinations yet</h3>
            <p className="fd-empty-text">New venues are being added soon.</p>
          </div>
        ) : (
          <div className="fd-grid">
            {destinationsData.map((destination, index) => (
              <motion.article
                key={destination.id || destination.name || index}
                className="fd-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: Math.min(index, 8) * 0.08 }}
              >
                <a href={destination.link} className="fd-card-link">
                  <div className="fd-card-media">
                    <img
                      src={destination.image}
                      alt={destination.name}
                      className="fd-card-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src =
                          'https://via.placeholder.com/800x1000?text=No+Image';
                      }}
                    />
                    <span className="fd-card-veil" aria-hidden="true" />

                    {destination.packagesCount > 0 && (
                      <span className="fd-card-badge">
                        {destination.packagesCount}{' '}
                        {destination.packagesCount === 1 ? 'Package' : 'Packages'}
                      </span>
                    )}

                    <div className="fd-card-body">
                      {(destination.city || destination.state) && (
                        <span className="fd-card-loc">
                          <FiMapPin aria-hidden="true" />
                          {[destination.city, destination.state]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      )}

                      <h3 className="fd-card-title">{destination.name}</h3>

                      {destination.shortDescription && (
                        <p className="fd-card-desc">{destination.shortDescription}</p>
                      )}

                      <span className="fd-card-cta">
                        <span className="fd-card-cta-text">Explore Venue</span>
                        <FiArrowRight className="fd-card-cta-icon" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        )}

        {/* ---------- View All ---------- */}
        <motion.div
          className="fd-view-all"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <a href="/wedding-destinations" className="fd-view-all-link">
            <span>View All Destinations</span>
            <FiArrowRight className="fd-view-all-icon" aria-hidden="true" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}